import {
  Component,
  ElementRef,
  HostListener,
  Renderer2,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription, debounceTime, first, fromEvent, map } from 'rxjs';
import { SearchableAlphabets } from 'src/app/core/constants';
import { SearchableAlphabetsPa } from 'src/app/core/constants';
import { SignificantTermList } from 'src/app/core/models';
import { DashboardService } from 'src/app/services/dashboard.service';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
  selector: 'app-significant-terms-detail',
  templateUrl: './significant-terms-detail.component.html',
  styleUrls: ['./significant-terms-detail.component.scss'],
})
export class SignificantTermsDetailComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput: ElementRef | undefined;
  isHandsetPortrait: boolean = false;
  isSignificantTermsLoading: boolean = false;
  significantTerms: SignificantTermList[] = [];
  significantTerm: SignificantTermList | null = null;
  significantTermName: string | null = '';
  subscription: Subscription[] = [];
  totalTerms: number = 0;
  searchableAlphabets: string[] = SearchableAlphabets;
  searchableAlphabetsPa: string[] = SearchableAlphabetsPa;
  activeAlphabetCriteriaIndex: number = 0;
  hashtagSearch: string = '';
  closeResultsCount: boolean = false;
  isGlobalSearchMode: boolean = false;
  searchTerm: string = '';
  availableInitials: string[] = [];
  unavailableCharsArray: string[] = [];
  initAlphabetCheck: boolean = false;
  constructor(
    private dashboardService: DashboardService,
    private route: ActivatedRoute,
    private renderer: Renderer2,
    private utils: UtilsService,
    private router: Router,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.subscription.push(
      this.route.paramMap.subscribe((val: Params) => {
        this.significantTermName = val['params'].significantTermName;
      })
    );
    let currentLanguage = localStorage.getItem('language');
    if (currentLanguage) {
      this.searchableAlphabets =
        currentLanguage === 'en'
          ? this.searchableAlphabets
          : this.searchableAlphabetsPa;
    }
    if (this.significantTermName != null || this.significantTermName !== '') {
      this.fetchSignificantTerm();
    } else {
      this.fetchSignificantTermsList();
    }
  }
  removeAllOtherTooltips(except?: HTMLElement) {
    const elements =
      this.elementRef.nativeElement.querySelectorAll('.tooltiptext');

    elements.forEach((elem: HTMLElement) => {
      if (elem !== except) {
        this.renderer.removeClass(elem, 'is-visible');
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // Find the closest parent having your icon class
    const iconElement = target.closest('.show-footnote-icon') as HTMLElement;

    if (iconElement) {
      const parentElement = iconElement.parentElement;
      if (!parentElement) return; // safety check

      const childElement = parentElement.querySelector(
        '.tooltiptext'
      ) as HTMLElement;
      if (!childElement) return; // safety check

      // Positioning logic
      if (this.isElementInLeftHalf(parentElement)) {
        this.renderer.addClass(childElement, 'tt-right-positioned');
        this.renderer.removeClass(childElement, 'tt-left-positioned');
      } else {
        this.renderer.addClass(childElement, 'tt-left-positioned');
        this.renderer.removeClass(childElement, 'tt-right-positioned');
      }

      // Hide all other tooltips first
      this.removeAllOtherTooltips(childElement);

      // Show current tooltip
      this.renderer.addClass(childElement, 'is-visible');
    } else {
      // Click outside → hide all tooltips
      this.removeAllOtherTooltips();
    }
  }
  isElementInLeftHalf(element: any): boolean {
    const { left, width } = element.getBoundingClientRect();
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    return [left, left + width][1] < viewportWidth / 2;
  }

  ngAfterViewInit() {
    const searchKeyUp$ = fromEvent(this.searchInput?.nativeElement, 'keyup');
    searchKeyUp$
      .pipe(
        map((i: any) => i.currentTarget.value),
        debounceTime(500)
      )
      .subscribe({
        next: (searchedTerm: string) => {
          this.activeAlphabetCriteriaIndex = 0;
          this.hashtagSearch = '';
          this.fetchSignificantTermsList();
        },
        error: (keyUpError) => {
          // console.log('key up error ', keyUpError);
        },
      });
  }

  fetchSignificantTermsList() {
    this.isSignificantTermsLoading = true;

    this.dashboardService
      .fetchSignificantTermsList(
        this.isHandsetPortrait,
        this.activeAlphabetCriteriaIndex,
        this.hashtagSearch,
        this.searchTerm
      )
      .pipe(first())
      .subscribe({
        next: (res: any) => {
         // console.log(res);
          if (this.initAlphabetCheck) {
            this.isGlobalSearchMode = true;
            this.isSignificantTermsLoading = false;
          }
          this.significantTerms = res?.value?.length ? res.value : [];
          this.totalTerms = this.significantTerms.length;

          this.initAlphabetCheck = true;
          if (
            !this.activeAlphabetCriteriaIndex &&
            this.significantTerms.length
          ) {
            this.unavailableCharsArray =
              this.utils.createUnavailableCharsArrayFromProperty(
                'WordName',
                this.significantTerms
              );
          }
          this.isSignificantTermsLoading = false;
        },
        error: (e: any) => {
          this.significantTerms = [];
          // console.error('error in fetching significantTerms', e);
        },
      });
  }

  fetchSignificantTerm() {
    this.isSignificantTermsLoading = true;
    this.dashboardService
      .fetchSignificantTerm(this.significantTermName)
      .pipe(first())
      .subscribe({
        next: (res: any) => {
          this.isGlobalSearchMode = false;
          this.isSignificantTermsLoading = false;
          this.significantTerm = res?.value?.length ? res.value[0] : [];
        },
        error: (e: any) => {
          this.significantTerm = null;
          // console.error('error in fetching significantTerms', e);
        },
      });
  }

  addBrToDescriptionString(htmlString: any | undefined) {
    if (htmlString) {
      let modifiedHTML = htmlString?.slice();

      modifiedHTML = modifiedHTML.replace(/\n/g, '<br>');

      return modifiedHTML;
    }
  }
  showSmallerIntroductionString(descriptionText: string | undefined) {
    //descriptionText = descriptionText.replace(/\n/g, '<br>');

    return this.utils.showSmallerTermString(descriptionText);
  }

  goToTermDetail(termName: string) {
    this.significantTermName = termName;
    this.fetchSignificantTerm();
    this.router.navigateByUrl(`/significant-term-detail/${termName}`, {
      skipLocationChange: false,
      replaceUrl: true,
    });
  }

  setAlphabetCriteria(activeIndex: number) {
    if (
      this.unavailableCharsArray.indexOf(
        this.searchableAlphabets[activeIndex]
      ) !== -1
    )
      return;
    if (this.activeAlphabetCriteriaIndex !== activeIndex) {
      this.activeAlphabetCriteriaIndex = activeIndex;
    }
    sessionStorage.setItem('glossaryIndex', activeIndex.toString());
    this.router.navigate(['/glossary']);

    // this.fetchSignificantTermsList();
  }

  searchByHashtag(hashtag: string, e: Event) {
    // e.stopPropagation()
    this.isGlobalSearchMode = true;
    this.significantTerms = [];
    this.significantTermName = '';
    this.hashtagSearch = hashtag;
    this.activeAlphabetCriteriaIndex = 0;
    this.fetchSignificantTermsList();
  }

  fetchTags(attributesString: string | undefined) {
    let attributesArray: any[] = [];

    if (attributesString?.trim() && attributesString.length) {
      attributesArray = attributesString.split(',');
    }
    return attributesArray;
  }

  isHandsetPortraitStatus(status: boolean): void {
    this.isHandsetPortrait = status;
  }

  ngOnDestroy(): void {
    this.subscription.forEach((x) => x.unsubscribe());
  }
}
